import React from 'react';
import { ui } from '../../config/uiSizes';
import { formatUtcTimestampLocal, getLocalTimezoneAbbr } from '../../utils/formatters';
import { formatRegion } from '../../utils/formatters';
import { getSchedule } from '../../utils/cron';
import { isRollbackEnabled } from '../../utils/appEnvironment';
import { hasCompletionWarnings } from '../../utils/migrationWarnings';
import type { Migration } from '../../types';
import type { Organization } from '../../hooks/api/useFetchOrganizations';
import RunningIcon from '../../assets/running7-icon.png';
import CompletedIcon from '../../assets/completed1-icon.png';
import FailedIcon from '../../assets/failed-icon.png';
import QueuedIcon from '../../assets/Queued2.png';
import CompletedApprovalIcon from '../../assets/completedApproval1.png';
import tranformationApprovalIcon from '../../assets/transformationApproval8.png';
import RollbackPlanApprovalIcon from '../../assets/rollbackPlanApproval3.png';

interface TableRowProps {
  job: Migration;
  organizations: Organization[];
  onViewClick: (job: Migration) => void;
  onAttentionClick: (job: Migration) => void;
  onDeleteClick: (job: Migration) => void;
  onApproveClick: (job: Migration) => void;
  onRollbackClick?: (job: Migration) => void;
  onReviewClick?: (job: Migration) => void;
}

export const TableRow: React.FC<TableRowProps> = ({
  job,
  organizations,
  onViewClick,
  onAttentionClick,
  onDeleteClick,
  onApproveClick,
  onRollbackClick,
  onReviewClick
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [showCronTooltip, setShowCronTooltip] = React.useState(false);
  const iconRef = React.useRef<HTMLDivElement>(null);
  const cronIconRef = React.useRef<HTMLDivElement>(null);

  const handleMouseMove = () => {
    if (iconRef.current) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const parseCronExpression = (cron: string): { days: string; time: string } => {
    const schedule = getSchedule(cron);

    if (!schedule || schedule.hour == null || schedule.minute == null) {
      return { days: 'Invalid', time: 'Invalid' };
    }

    const dayNames = schedule.days
      .map((dayCode) => `${dayCode.charAt(0)}${dayCode.slice(1).toLowerCase()}`)
      .join(', ');
    const formattedTime = `${schedule.hour}:${schedule.minute.toString().padStart(2, '0')} ${schedule.ampm} ${getLocalTimezoneAbbr()}`;

    return {
      days: dayNames || 'Every day',
      time: formattedTime
    };
  };

  const getStatusContent = () => {
    switch (job.status) {
      case 'running':
        return {
          icon: RunningIcon,
          title: 'In Progress',
          description:
            'This migration is currently running. Status will update once execution completes.',
          iconBg: 'bg-blue-100 dark:bg-blue-700',
          iconColor: 'text-blue-600 dark:text-blue-200',
          borderColor: 'border-blue-300 dark:border-blue-700',
          symbol: '⟳'
        };
      case 'completed':
        return {
          icon: CompletedIcon,
          title: 'Completed Successfully',
          description: 'This migration has completed successfully with no errors.',
          iconBg: 'bg-green-100 dark:bg-green-700',
          iconColor: 'text-green-600 dark:text-green-200',
          borderColor: 'border-green-300 dark:border-green-700',
          symbol: '✓'
        };
      case 'PartiallyCompleted':
        return {
          icon: FailedIcon,
          title: 'Migration Partially Completed',
          description:
            'Some resources imported successfully, but others failed. Click Attention to review which resources failed and why.',
          iconBg: 'bg-amber-100 dark:bg-amber-700',
          iconColor: 'text-amber-600 dark:text-amber-200',
          borderColor: 'border-amber-300 dark:border-amber-700',
          symbol: '⚠'
        };
      case 'failed':
        if (job.isSnapshot) {
          return {
            icon: FailedIcon,
            title: 'Snapshot Errors Received',
            description:
              'Errors occurred during the last snapshot run. Click Attention to review the error details.',
            iconBg: 'bg-red-100 dark:bg-red-700',
            iconColor: 'text-red-600 dark:text-red-200',
            borderColor: 'border-red-300 dark:border-red-700',
            symbol: '!'
          };
        }
        return {
          icon: FailedIcon,
          title: 'Migration Errors Received',
          description:
            'Errors occurred during the last migration run. Click Attention to review the error details.',
          iconBg: 'bg-red-100 dark:bg-red-700',
          iconColor: 'text-red-600 dark:text-red-200',
          borderColor: 'border-red-300 dark:border-red-700',
          symbol: '!'
        };
      case 'ExportFailed':
        return {
          icon: FailedIcon,
          title: 'Export Failed Errors Received',
          description:
            'Errors occurred during the Export step in the last migration run. Click Attention to review the error details.',
          iconBg: 'bg-red-100 dark:bg-red-700',
          iconColor: 'text-red-600 dark:text-red-200',
          borderColor: 'border-red-300 dark:border-red-700',
          symbol: '!'
        };
      case 'ExportQueued':
        return {
          icon: QueuedIcon,
          title: 'Export Queued',
          description:
            'This export has been queued successfully and is waiting to start. The system will automatically begin processing once the previous exports complete or resources become available.',
          iconBg: 'bg-gray-100 dark:bg-gray-700',
          iconColor: 'text-gray-600 dark:text-gray-200',
          borderColor: 'border-gray-300 dark:border-gray-700',
          symbol: '⌚'
        };
      case 'ExportInProgress':
        return {
          icon: RunningIcon,
          title: 'Export In Progress',
          description:
            'This export is currently in progress. Status will update once execution completes.',
          iconBg: 'bg-blue-100 dark:bg-blue-700',
          iconColor: 'text-blue-600 dark:text-blue-200',
          borderColor: 'border-blue-300 dark:border-blue-700',
          symbol: '⟳'
        };
      case 'ExportComplete':
        return {
          icon: CompletedApprovalIcon,
          title: 'Export Completed',
          description:
            'This export has completed successfully with no errors. And is pending approval to proceed to the next step.',
          iconBg: 'bg-green-100 dark:bg-green-700',
          iconColor: 'text-green-600 dark:text-green-200',
          borderColor: 'border-green-300 dark:border-green-700',
          symbol: '👤'
        };
      case 'TransformationFailed':
        return {
          icon: FailedIcon,
          title: 'Transformation Failed Errors Received',
          description:
            'Errors occurred during the Transformation step in the last transformation run. Click View to review the error details.',
          iconBg: 'bg-red-100 dark:bg-red-700',
          iconColor: 'text-red-600 dark:text-red-200',
          borderColor: 'border-red-300 dark:border-red-700',
          symbol: '!'
        };
      case 'ImportFailed':
        return {
          icon: FailedIcon,
          title: 'Import Failed Errors Received',
          description:
            'Errors occurred during the Import step. Click Attention to review the error details.',
          iconBg: 'bg-red-100 dark:bg-red-700',
          iconColor: 'text-red-600 dark:text-red-200',
          borderColor: 'border-red-300 dark:border-red-700',
          symbol: '!'
        };
      case 'ValidationFailed':
        return {
          icon: FailedIcon,
          title: 'Validation Failed',
          description:
            'Validation failed during the Import step. Click Attention to review the error details.',
          iconBg: 'bg-red-100 dark:bg-red-700',
          iconColor: 'text-red-600 dark:text-red-200',
          borderColor: 'border-red-300 dark:border-red-700',
          symbol: '!'
        };
      case 'TransformationQueued':
        return {
          icon: QueuedIcon,
          title: 'Transformation Queued',
          description:
            'This transformation has been queued successfully and is waiting to start. The system will automatically begin processing once the previous transformations complete or resources become available.',
          iconBg: 'bg-gray-100 dark:bg-gray-700',
          iconColor: 'text-gray-600 dark:text-gray-200',
          borderColor: 'border-gray-300 dark:border-gray-700',
          symbol: '⌚'
        };
      case 'TransformationInProgress':
        return {
          icon: RunningIcon,
          title: 'Transformation In Progress',
          description:
            'This transformation is currently in progress. Status will update once execution completes.',
          iconBg: 'bg-blue-100 dark:bg-blue-700',
          iconColor: 'text-blue-600 dark:text-blue-200',
          borderColor: 'border-blue-300 dark:border-blue-700',
          symbol: '⟳'
        };
      case 'TransformationComplete':
        return {
          icon: tranformationApprovalIcon,
          title: 'Transformation Complete',
          description:
            'This transformation has completed successfully with no errors. And is pending approval to proceed to the next step.',
          iconBg: 'bg-blue-100 dark:bg-blue-700',
          iconColor: 'text-blue-600 dark:text-blue-200',
          borderColor: 'border-blue-300 dark:border-blue-700',
          symbol: '👤'
        };
      case 'ImportQueued':
        return {
          icon: QueuedIcon,
          title: 'Import Queued',
          description:
            'This import has been queued successfully and is waiting to start. The system will automatically begin processing once the previous imports complete or resources become available.',
          iconBg: 'bg-gray-100 dark:bg-gray-700',
          iconColor: 'text-gray-600 dark:text-gray-200',
          borderColor: 'border-gray-300 dark:border-gray-700',
          symbol: '⌚'
        };
      case 'ImportInProgress':
        return {
          icon: RunningIcon,
          title: 'Import In Progress',
          description:
            'This import is currently in progress. Status will update once execution completes.',
          iconBg: 'bg-blue-100 dark:bg-blue-700',
          iconColor: 'text-blue-600 dark:text-blue-200',
          borderColor: 'border-blue-300 dark:border-blue-700',
          symbol: '⟳'
        };

      case 'SnapshotCompleted':
        return {
          icon: CompletedIcon,
          title: 'Snapshot Completed Successfully',
          description: 'This Snapshot has completed successfully with no errors.',
          iconBg: 'bg-green-100 dark:bg-green-700',
          iconColor: 'text-green-600 dark:text-green-200',
          borderColor: 'border-green-300 dark:border-green-700',
          symbol: '✓'
        };

      // Rollback pipeline stages
      case 'RollbackQueued':
        return {
          icon: QueuedIcon,
          title: 'Rollback Queued',
          description:
            'A rollback has been requested and is queued for setup. The system will begin generating a rollback plan shortly.',
          iconBg: 'bg-gray-100 dark:bg-gray-700',
          iconColor: 'text-gray-600 dark:text-gray-200',
          borderColor: 'border-gray-300 dark:border-gray-700',
          symbol: '⌚'
        };
      case 'RollbackSetupInProgress':
        return {
          icon: RunningIcon,
          title: 'Rollback Setup In Progress',
          description:
            'The rollback setup job is running and generating a Terraform plan. This may take a few moments.',
          iconBg: 'bg-blue-100 dark:bg-blue-700',
          iconColor: 'text-blue-600 dark:text-blue-200',
          borderColor: 'border-blue-300 dark:border-blue-700',
          symbol: '⟳'
        };
      case 'RollbackPlanAwaitingApproval':
        return {
          icon: RollbackPlanApprovalIcon,
          title: 'Rollback Plan Awaiting Approval',
          description:
            'The rollback plan has been generated and is awaiting your approval. Review the plan and confirm to proceed.',
          iconBg: 'bg-teal-100 dark:bg-teal-700',
          iconColor: 'text-teal-600 dark:text-teal-200',
          borderColor: 'border-teal-300 dark:border-teal-700',
          symbol: '👤'
        };
      case 'RollbackInProgress':
        return {
          icon: RunningIcon,
          title: 'Rollback In Progress',
          description:
            'The rollback is currently applying changes to your target environment. Status will update once execution completes.',
          iconBg: 'bg-blue-100 dark:bg-blue-700',
          iconColor: 'text-blue-600 dark:text-blue-200',
          borderColor: 'border-blue-300 dark:border-blue-700',
          symbol: '⟳'
        };
      case 'RollbackComplete':
        return {
          icon: CompletedIcon,
          title: 'Rollback Completed Successfully',
          description: 'The rollback has completed successfully. All resources have been restored to their pre-migration state.',
          iconBg: 'bg-green-100 dark:bg-green-700',
          iconColor: 'text-green-600 dark:text-green-200',
          borderColor: 'border-green-300 dark:border-green-700',
          symbol: '✓'
        };
      case 'RollbackPartiallyCompleted':
        return {
          icon: FailedIcon,
          title: 'Rollback Partially Completed',
          description:
            'The rollback completed with mixed results. Some resources were restored successfully, but others could not be reverted. Review the details for more information.',
          iconBg: 'bg-amber-100 dark:bg-amber-700',
          iconColor: 'text-amber-600 dark:text-amber-200',
          borderColor: 'border-amber-300 dark:border-amber-700',
          symbol: '⚠'
        };
      case 'RollbackFailed':
        return {
          icon: FailedIcon,
          title: 'Rollback Failed',
          description:
            'The rollback encountered errors and no resources were successfully restored. Click View to review the error details.',
          iconBg: 'bg-red-100 dark:bg-red-700',
          iconColor: 'text-red-600 dark:text-red-200',
          borderColor: 'border-red-300 dark:border-red-700',
          symbol: '!'
        };

      default:
        // Default case for any unmapped statuses - show as running/in-progress
        return {
          icon: RunningIcon,
          title: job.status,
          description: `Status: ${job.status}`,
          iconBg: 'bg-gray-100 dark:bg-gray-700',
          iconColor: 'text-gray-600 dark:text-gray-200',
          borderColor: 'border-gray-300 dark:border-gray-700',
          symbol: '•'
        };
    }
  };

  const statusContent = getStatusContent();
  const isFailedStatus =
    job.status === 'failed' ||
    (typeof job.status === 'string' && job.status.toLowerCase().includes('failed'));

  // A dry-run preview applies nothing, so it must never render as a partial failure / manual-steps row
  // or offer Rollback (there is nothing to roll back) — mirror the View/Attention modal gating.
  const isDryRunPreview = !job.isSnapshot && job.job3Import?.dryRun === true;

  // A completed migration can still carry manual-remediation warnings (e.g. an IVR that
  // could not be moved because its dependency flow was unresolvable). These require the same
  // operator attention as a partial failure, so they route to the Attention modal.
  const isCompletionWarningStatus = !isDryRunPreview && hasCompletionWarnings(job);

  // A migration that imported some resources and failed others is a mixed outcome.
  // It is not a success — it requires attention like a failure, but is rendered amber.
  const isPartiallyCompletedStatus =
    !job.isSnapshot && String(job.status) === 'PartiallyCompleted';
  const isApprovalStatus = job.status === 'ExportComplete';
  const isTransformationApprovedStatus = job.status === 'TransformationComplete';

  // Show Rollback badge for any row whose status is a rollback pipeline stage,
  // including real backend rows and legacy mock demo rows.
  const isRollbackRow =
    (typeof job.status === 'string' && job.status.startsWith('Rollback')) ||
    (
      typeof job.id === 'string' &&
      job.id.startsWith('mock-rollback-') &&
      ['PartiallyCompleted', 'failed'].includes(String(job.status))
    );

  const isAwaitingApproval = job.status === 'RollbackPlanAwaitingApproval';

  // RollbackFailed / RollbackPartiallyCompleted → Attention + Delete
  const isRollbackErrorStatus =
    job.status === 'RollbackFailed' || job.status === 'RollbackPartiallyCompleted';

  // Remaining rollback statuses (Queued, SetupInProgress, InProgress, Complete) → View + Delete
  const isOtherRollbackRow =
    isRollbackRow && !isAwaitingApproval && !isRollbackErrorStatus;

  // Rollback availability window: show Rollback button for completed non-snapshot
  // migrations where import finished <= 5 days ago. After 5 days, show Delete.
  // Keep in sync with the backend RollbackSetup:RollbackAvailabilityWindowDays default (5).
  const rollbackWindowDays = 5;
  const completedAt =
    !job.isSnapshot && job.status.toString() === 'completed'
      ? (job.job3Import?.completedAtUtc ?? job.updatedAtUtc ?? null)
      : null;
  const daysSinceCompletion =
    completedAt != null
      ? Math.floor((Date.now() - new Date(completedAt).getTime()) / (1000 * 60 * 60 * 24))
      : null;
  const isWithinRollbackWindow = daysSinceCompletion !== null && daysSinceCompletion <= rollbackWindowDays;
  const rollbackEnabled = isRollbackEnabled() && !isDryRunPreview;

  // When a completed migration carries manual-remediation warnings, present it as a warning
  // (amber icon + Attention button) instead of a clean success.
  if (isCompletionWarningStatus && statusContent) {
    statusContent.icon = FailedIcon;
    statusContent.title = 'Completed — Manual Steps Required';
    statusContent.description =
      'This migration completed, but some resources could not be automatically migrated. Click Attention to review the required manual steps.';
    statusContent.iconBg = 'bg-yellow-100 dark:bg-yellow-700';
    statusContent.iconColor = 'text-yellow-600 dark:text-yellow-200';
    statusContent.borderColor = 'border-yellow-300 dark:border-yellow-700';
    statusContent.symbol = '⚠';
  }

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
      <td className={`${ui.page.dashboardCell} ${ui.text.body} font-medium text-gray-900 dark:text-gray-100`}>
        <div className={`w-full ${ui.text.tableCell} rounded-lg bg-gray-100 dark:bg-gray-700 ${ui.page.nameCellContent} font-bold text-gray-900 dark:text-gray-100 shadow-sm flex items-center ${ui.gap.compact} cursor-default min-w-0 overflow-hidden`}>
          <span className="truncate" title={job.name}>{job.name}</span>
          {job.snapshotCronExpression && (
            <div
              className="relative inline-flex"
              ref={cronIconRef}
              onMouseEnter={() => setShowCronTooltip(true)}
              onMouseLeave={() => setShowCronTooltip(false)}
            >
              <svg
                className={`${ui.svg.xs} text-blue-600 dark:text-blue-400 cursor-help`}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {showCronTooltip && job.snapshotCronExpression && (
                <div
                  className={`fixed z-[9999] w-56 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl ${ui.page.smPadding} pointer-events-none`}
                  style={{
                    left: cronIconRef.current
                      ? (() => {
                        const rect = cronIconRef.current!.getBoundingClientRect();
                        return rect.right + 8 + 224 > window.innerWidth
                          ? `${rect.left - 224 - 8}px`
                          : `${rect.right + 8}px`;
                      })()
                      : '0px',
                    top: cronIconRef.current
                      ? `${cronIconRef.current.getBoundingClientRect().top}px`
                      : '0px'
                  }}
                >
                  <h4 className={`${ui.text.body} font-semibold text-blue-600 dark:text-blue-400 ${ui.mb.sm}`}>
                    {job.isSnapshot ? 'Recurring Snapshot Schedule' : 'Recurring Migration Schedule'}
                  </h4>
                  <div className={`${ui.text.xs} text-gray-700 dark:text-gray-300 space-y-1`}>
                    <div>
                      <span className="font-semibold">Days:</span>{' '}
                      {parseCronExpression(job.snapshotCronExpression).days}
                    </div>
                    <div>
                      <span className="font-semibold">Time:</span>{' '}
                      {parseCronExpression(job.snapshotCronExpression).time}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </td>
      <td className={`${ui.page.dashboardCell} ${ui.text.tableCell} font-medium text-gray-900 dark:text-gray-100`}>
        <div className="flex flex-wrap items-center gap-1">
          {isRollbackRow ? (
            <span
              className={`inline-flex items-center ${ui.badge.padding} rounded-full ${ui.text.tableCell} font-semibold bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300`}
            >
              Rollback
            </span>
          ) : (
            <span
              className={`inline-flex items-center ${ui.badge.padding} rounded-full ${ui.text.tableCell} font-semibold ${job.isSnapshot
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
                }`}
            >
              {job.isSnapshot ? 'Snapshot' : 'Migration'}
            </span>
          )}
        </div>
      </td>
      <td className={`${ui.page.dashboardCell} ${ui.text.tableCell} text-gray-900 dark:text-gray-100`}>
        <div className="w-full truncate cursor-default select-none" title={job.createdBy}>
          {job.createdBy}
        </div>
      </td>
      <td className={`${ui.page.dashboardCell} ${ui.text.tableCell} text-gray-900 dark:text-gray-100`}>
        <div className="flex flex-col gap-0.5 cursor-default select-none">
          <div className="font-medium truncate" title={organizations.find(o => o.id === job.sourceOrgId)?.name ?? job.sourceOrgName}>
            {organizations.find(o => o.id === job.sourceOrgId)?.name ?? job.sourceOrgName}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate" title={formatRegion(organizations.find(o => o.id === job.sourceOrgId)?.region)}>
            {formatRegion(organizations.find(o => o.id === job.sourceOrgId)?.region)}
          </div>
        </div>
      </td>
      <td className={`${ui.page.dashboardCell} ${ui.text.tableCell} text-gray-900 dark:text-gray-100`}>
        {job.isSnapshot ? (
          <div className="w-full truncate cursor-default select-none" title={organizations.find(o => o.id === job.targetOrgId)?.name ?? job.targetOrgName}>
            {organizations.find(o => o.id === job.targetOrgId)?.name ?? job.targetOrgName}
          </div>
        ) : (
          <div className="flex flex-col gap-0.5 cursor-default select-none">
            <div className="font-medium truncate" title={organizations.find(o => o.id === job.targetOrgId)?.name ?? job.targetOrgName}>
              {organizations.find(o => o.id === job.targetOrgId)?.name ?? job.targetOrgName}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate" title={formatRegion(organizations.find(o => o.id === job.targetOrgId)?.region)}>
              {formatRegion(organizations.find(o => o.id === job.targetOrgId)?.region)}
            </div>
          </div>
        )}
      </td>
      <td className={`${ui.page.dashboardCell} ${ui.text.tableCell} text-gray-900 dark:text-gray-100`}>
        {(() => {
          const lastRun =
            job.updatedAtUtc && new Date(job.updatedAtUtc) > new Date(job.createdAtUtc)
              ? job.updatedAtUtc
              : job.createdAtUtc;
          const formatted = formatUtcTimestampLocal(lastRun);
          return (
            <div className="w-full truncate cursor-default select-none" title={formatted}>
              {formatted}
            </div>
          );
        })()}
      </td>
      <td className={`${ui.page.statusCell} w-14 text-gray-900 dark:text-gray-100`}>
        {statusContent && (
          <div
            className="relative inline-flex items-center"
            ref={iconRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <img
              src={statusContent.icon}
              alt={job.status.toString()}
              className={`cursor-default ${(isFailedStatus || isPartiallyCompletedStatus || isCompletionWarningStatus) ? ui.widthAndHeight.statusIconFailed : ui.widthAndHeight.statusIcon}`}
            />
            {showTooltip && (
              <div
                className={`fixed z-[9999] w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl ${ui.page.smPadding} pointer-events-none`}
                style={{
                  left: iconRef.current
                    ? (() => {
                      const rect = iconRef.current!.getBoundingClientRect();
                      return rect.right + 8 + 256 > window.innerWidth
                        ? `${rect.left - 256 - 8}px`
                        : `${rect.right + 8}px`;
                    })()
                    : '0px',
                  top: iconRef.current ? `${iconRef.current.getBoundingClientRect().top}px` : '0px'
                }}
              >
                <div className={`flex items-start ${ui.gap.sm}`}>
                  <div
                    className={`flex-shrink-0 ${ui.widthAndHeight.iconContainerSm} ${statusContent.iconBg} rounded-full flex items-center justify-center`}
                  >
                    <span className={`${statusContent.iconColor} font-bold ${ui.text.body}`}>
                      {statusContent.symbol}
                    </span>
                  </div>
                  <div>
                    <h4 className={`${ui.text.body} font-semibold ${statusContent.iconColor} mb-1`}>
                      {statusContent.title}
                    </h4>
                    <p className={`${ui.text.xs} text-gray-700 dark:text-gray-300`}>
                      {statusContent.description}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </td>

      <td className={`${ui.page.actionCell} whitespace-nowrap`}>
        {isAwaitingApproval ? (
          <div className={`grid grid-cols-2 ${ui.gap.buttonGrid}`}>
            <button
              className={`rounded-full bg-teal-100 text-teal-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-teal-200 dark:bg-teal-800 dark:text-teal-200 dark:hover:bg-teal-700`}
              onClick={() => onReviewClick?.(job)}
            >
              Review
            </button>
            <button
              className={`rounded-full bg-red-100 text-red-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700`}
              onClick={() => onDeleteClick(job)}
            >
              Delete
            </button>
          </div>
        ) : (
          <div className={`grid grid-cols-2 ${ui.gap.buttonGrid}`}>
            {isRollbackErrorStatus ? (
              <button
                className={`rounded-full bg-orange-100 text-orange-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-orange-200 dark:bg-orange-700 dark:text-orange-200 dark:hover:bg-orange-600`}
                onClick={() => onAttentionClick(job)}
              >
                Attention
              </button>
            ) : isOtherRollbackRow ? (
              <button
                className={`rounded-full bg-gray-100 text-gray-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-700`}
                onClick={() => onViewClick(job)}
              >
                View
              </button>
            ) : (isFailedStatus || isPartiallyCompletedStatus || isCompletionWarningStatus) ? (
              <button
                className={`rounded-full bg-orange-100 text-orange-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-orange-200 dark:bg-orange-700 dark:text-orange-200 dark:hover:bg-orange-600`}
                onClick={() => onAttentionClick(job)}
              >
                Attention
              </button>
            ) : isApprovalStatus ? (
              <button
                className={`rounded-full bg-emerald-100 text-emerald-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-emerald-200 dark:bg-emerald-800 dark:text-emerald-200 dark:hover:bg-emerald-700`}
                onClick={() => onApproveClick(job)}
              >
                Review
              </button>
            ) : isTransformationApprovedStatus ? (
              <button
                className={`rounded-full bg-blue-100 text-blue-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-blue-200 dark:bg-blue-800 dark:text-blue-200 dark:hover:bg-blue-700`}
                onClick={() => onApproveClick(job)}
              >
                Review
              </button>
            ) : (
              <button
                className={`rounded-full bg-gray-100 text-gray-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-700`}
                onClick={() => onViewClick(job)}
              >
                View
              </button>
            )}
            {rollbackEnabled && isWithinRollbackWindow ? (
              <button
                className={`rounded-full bg-indigo-100 text-indigo-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-indigo-200 dark:bg-indigo-900 dark:text-indigo-300 dark:hover:bg-indigo-800`}
                onClick={() => onRollbackClick?.(job)}
              >
                Rollback
              </button>
            ) : (
              <button
                className={`rounded-full bg-red-100 text-red-700 ${ui.button.pill} font-semibold shadow-md transition hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700`}
                onClick={() => onDeleteClick(job)}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};
